import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveReturnTo } from '../lib/returnTo';

export const ProtectedRoute = ({ children, allowedRoles, allowSharedCheckin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If we're about to bounce an unauthenticated visitor to /login, remember
  // exactly which URL they were trying to reach so we can bring them back
  // after they sign in (instead of dumping them at /dashboard).
  useEffect(() => {
    if (!loading && !user) {
      saveReturnTo(`${location.pathname}${location.search}${location.hash}`);
    }
  }, [loading, user, location]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // The shared General Check-in login may only use the today-only check-in portal.
  if (user.is_shared_checkin && !allowSharedCheckin) {
    return <Navigate to="/coach/checkin" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Role mismatch — send them somewhere they belong.
    //   • Admin & coach → their own sidebar dashboard
    //   • Everyone else (students, guests) → the public homepage. The Student
    //     Portal has been removed (13 D 14), so never send anyone to /dashboard
    //     unless they're admin or coach.
    if (user.role === 'admin' || user.role === 'coach') {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};
